import { buildPath, Repo } from "@thenounproject/lingo-core";
import { fetchKitVersion, fetchSection, pollForDuplicateKit } from "../actions/actions";
import { showNotification } from "@actions/useShowNotification";
import * as actionTypes from "../actions/actionTypes";
import { fetchItem } from "../actions/assets";

const KIT_VERSION_DELAY = 1000;
const KIT_DUPLICATE_DELAY = 2500;
const PENDING_SECTION_DELAY = 1000;
const ASSET_POLL_DELAY = 5000;
let boundFetchVersion = null;

const polling = store => next => action => {
  kitVersionPolling(store, action);
  processingAssetPolling(store, action);
  kitDuplicationPolling(store, action);
  sectionDuplicationPolling(store, action);
  return next(action);
};

// MARK : Kit Duplication Polling
// -------------------------------------------------------------------------------
function kitDuplicationPolling(store, action) {
  function poll(kitId, sourceId, spaceId) {
    setTimeout(() => {
      store.dispatch(pollForDuplicateKit(kitId, sourceId, spaceId));
    }, KIT_DUPLICATE_DELAY);
  }

  if (actionTypes.DUPLICATE_KIT_SUCCESS === action.type) {
    const { response, sourceId } = action,
      version = response.entities.kitVersions[response.result];
    poll(version.kitId, sourceId, version.spaceId);
  } else if (actionTypes.POLL_FOR_DUPLICATE_KIT_FAILURE === action.type) {
    const { spaceId, kitId, sourceId } = action;
    poll(kitId, sourceId, spaceId);
  } else if (actionTypes.POLL_FOR_DUPLICATE_KIT_SUCCESS === action.type) {
    const kit = action.response.entities.kits[action.response.result];
    store.dispatch(
      showNotification({
        message: `“${kit.name}” is ready. `,
        link: {
          text: "View kit.",
          url: buildPath(`/k/${kit.kitId}/`, {
            space: { id: kit.spaceId },
          }),
        },

        level: "info",
      })
    );
  }
}

// MARK : Section Duplicate Polling
// -------------------------------------------------------------------------------
function sectionDuplicationPolling(store, action) {
  function poll(kitId, sourceId, spaceId) {
    setTimeout(() => {
      store.dispatch(fetchSection(spaceId, sourceId));
    }, PENDING_SECTION_DELAY);
  }

  const actions = [actionTypes.DUPLICATE_SECTION_SUCCESS, actionTypes.FETCH_SECTION_SUCCESS];
  if (actions.includes(action.type)) {
    const { response } = action,
      sections = Object.values(response.entities.sections).filter(s => s.status === "pending");
    if (sections.length) {
      const repo = new Repo(store.getState());
      sections.forEach(section => {
        const version = repo.getVersion(section.kitId, section.version);
        poll(version.spaceId, section.id);
      });
    }
  }
}

// MARK : Asset Processing Polling
// -------------------------------------------------------------------------------
/**
 * When an item is returned from an api call with a pending processing status, this middleware
 * dispatches another FETCH_ITEM action to check for updates to the item POLL_DELAY ms later.
 * It will stop polling when the item's processing status is no longer pending.
 */
function processingAssetPolling(store, action) {
  let items = [];

  if (
    [actionTypes.REPLACE_ASSET_FILE_SUCCESS, actionTypes.RETRY_ASSET_FILE_SUCCESS].includes(
      action.type
    )
  ) {
    // Replacing an asset only returns asset but we need to poll on the item
    const asset = action.response.entities.assets[action.response.result];
    items = Object.values(store.getState().entities.items || {}).filter(
      i => i.assetId === asset.id
    );
  } else {
    try {
      items = Object.values(action.response.entities.items).filter(
        item => action.response.entities.assets[item.asset]?.meta?.assetProcessing === "processing"
      );
    } catch (error) {
      // That's ok
    }
  }

  items.forEach(item => {
    const {
      id: itemId,
      spaceId,
      version,
      asset: { shareToken: assetToken },
    } = item;
    setTimeout(() => {
      store.dispatch(fetchItem(spaceId, itemId, version, assetToken));
    }, ASSET_POLL_DELAY);
  });
}

// MARK : Kit Version Polling
// -------------------------------------------------------------------------------

// Action creator
const fetchVersion = function (store, action) {
  document.removeEventListener("visibilitychange", boundFetchVersion, false);
  store.dispatch(fetchKitVersion(action.uuid, action.spaceId, action.version));
};

function kitVersionPolling(store, action) {
  boundFetchVersion = fetchVersion.bind(null, store, action);
  // Initiates the polling loop
  if (actionTypes.POLL_KIT_VERSION === action.type) {
    boundFetchVersion();
  }
  // Subsequent polling actions are scheduled after each FETCH_KIT_VERSION_SUCCESS action
  if (actionTypes.FETCH_KIT_VERSION_SUCCESS === action.type) {
    // ...unless the site has been hidden
    if (document.hidden) {
      document.addEventListener("visibilitychange", boundFetchVersion, false);
      return;
    }

    const kitVersion = action.response.entities.kitVersions[`${action.uuid}-${action.version}`];
    if (kitVersion && kitVersion.status === "pending") {
      setTimeout(boundFetchVersion, KIT_VERSION_DELAY);
    }
    if (kitVersion && kitVersion.status === "active") {
      const state = store.getState(),
        isModalOpen = Boolean(state.modals.length);
      if (!isModalOpen) {
        store.dispatch(
          showNotification({
            message: `“${kitVersion.version_identifier}” released. `,
            link: {
              text: "View version.",
              url: buildPath(`/k/${kitVersion.kitId}/?v=${kitVersion.version}`, {
                space: { id: kitVersion.spaceId },
              }),
            },

            level: "info",
          })
        );
      }
    }
  }
}

export default polling;
